import { useState, useCallback, useMemo } from 'react';
import { base44 } from '@/api/base44Client';

/**
 * Unified tagging hook for officers, artifacts, rituals, policies, and capabilities.
 * Replaces 12+ implementations of duplicated tagging logic across the app.
 * 
 * Usage:
 * const {
 *   tags,
 *   addTag,
 *   removeTag,
 *   setTags,
 *   resetTags,
 *   currentTagType,
 *   setCurrentTagType,
 *   getTagOptions,
 *   renderTagBadges,
 *   TAG_TYPES,
 *   TAG_STYLES
 * } = useEntityTagging({
 *   officers,
 *   artifacts,
 *   rituals,
 *   policies,
 *   capabilities,
 *   currentUserEmail,
 *   initialTags
 * });
 */

// Tag type constants
export const TAG_TYPES = ['Officer', 'Artifact', 'Ritual', 'Policy', 'Capability'];

// Field mapping for each tag type
export const TAG_FIELD_MAP = {
  'Officer': 'tagged_officers',
  'Artifact': 'tagged_artifacts',
  'Ritual': 'tagged_rituals',
  'Policy': 'tagged_policies',
  'Capability': 'tagged_capabilities'
};

// Reverse mapping (field name to tag type)
export const FIELD_TO_TAG_TYPE = {
  'tagged_officers': 'Officer',
  'tagged_artifacts': 'Artifact',
  'tagged_rituals': 'Ritual',
  'tagged_policies': 'Policy',
  'tagged_capabilities': 'Capability'
};

// Styling for each tag type
export const TAG_STYLES = {
  'Officer': 'bg-indigo-500/10 text-indigo-400 border-indigo-500/30',
  'Artifact': 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30',
  'Ritual': 'bg-purple-500/10 text-purple-400 border-purple-500/30',
  'Policy': 'bg-amber-500/10 text-amber-400 border-amber-500/30',
  'Capability': 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30'
};

// Default empty tags state
export const EMPTY_TAGS = {
  tagged_officers: [],
  tagged_artifacts: [],
  tagged_rituals: [],
  tagged_policies: [],
  tagged_capabilities: []
};

/**
 * Filter artifacts based on company secret visibility rules
 */
export function filterVisibleArtifacts(artifacts, userEmail) {
  if (!artifacts) return [];
  return artifacts.filter(a => {
    if (!a.is_company_secret) return true;
    if (a.owner_email === userEmail) return true;
    if ((a.editors || []).includes(userEmail)) return true;
    if ((a.viewers || []).includes(userEmail)) return true;
    return false;
  });
}

/**
 * Filter rituals based on company secret visibility rules
 */
export function filterVisibleRituals(rituals, userEmail) {
  if (!rituals) return [];
  return rituals.filter(r => {
    if (!r.is_company_secret) return true;
    if (r.owner_email === userEmail) return true;
    if ((r.participants || []).includes(userEmail)) return true;
    return false;
  });
}

/**
 * Main tagging hook
 */
export default function useEntityTagging({
  officers = [],
  artifacts = [],
  rituals = [],
  policies = [],
  capabilities = [],
  currentUserEmail = null,
  initialTags = null
} = {}) {
  // State for current tags
  const [tags, setTags] = useState(initialTags || { ...EMPTY_TAGS });
  
  // State for tag type selector
  const [currentTagType, setCurrentTagType] = useState('');

  // Memoized filtered lists based on company secret rules
  const visibleArtifacts = useMemo(
    () => filterVisibleArtifacts(artifacts, currentUserEmail),
    [artifacts, currentUserEmail]
  );

  const visibleRituals = useMemo(
    () => filterVisibleRituals(rituals, currentUserEmail),
    [rituals, currentUserEmail]
  );

  // Reset tags to initial or empty state
  const resetTags = useCallback((newInitialTags = null) => {
    setTags(newInitialTags || { ...EMPTY_TAGS });
    setCurrentTagType('');
  }, []);

  // Add a tag of specified type
  const addTag = useCallback((tagType, value) => {
    if (!tagType || !value) return;
    
    const fieldName = TAG_FIELD_MAP[tagType];
    if (!fieldName) return;

    setTags(prev => {
      // Check if already tagged
      if (prev[fieldName]?.includes(value)) {
        return prev;
      }
      return {
        ...prev,
        [fieldName]: [...(prev[fieldName] || []), value]
      };
    });
    
    setCurrentTagType('');
  }, []);

  // Remove a tag of specified type
  const removeTag = useCallback((tagType, value) => {
    const fieldName = TAG_FIELD_MAP[tagType];
    if (!fieldName) return;

    setTags(prev => ({
      ...prev,
      [fieldName]: (prev[fieldName] || []).filter(t => t !== value)
    }));
  }, []);

  // Get display label for a tag value
  const getTagLabel = useCallback((tagType, value) => {
    switch (tagType) {
      case 'Officer':
        const officer = officers.find(o => o.user_email === value);
        return officer?.callsign || value;
      case 'Artifact':
      case 'Ritual':
      case 'Policy':
      case 'Capability':
        return value;
      default:
        return value;
    }
  }, [officers]);

  // Get options for a tag type (filtered and not already selected)
  const getTagOptions = useCallback((tagType) => {
    const fieldName = TAG_FIELD_MAP[tagType];
    const currentTags = tags[fieldName] || [];

    switch (tagType) {
      case 'Officer':
        return officers
          .filter(o => !currentTags.includes(o.user_email))
          .map(o => ({ value: o.user_email, label: o.callsign }));
      
      case 'Artifact':
        return visibleArtifacts
          .filter(a => !currentTags.includes(a.name))
          .map(a => ({ value: a.name, label: a.name }));
      
      case 'Ritual':
        return visibleRituals
          .filter(r => !currentTags.includes(r.name))
          .map(r => ({ value: r.name, label: r.name }));
      
      case 'Policy':
        return (policies || [])
          .filter(p => !currentTags.includes(p.title))
          .map(p => ({ value: p.title, label: p.title }));
      
      case 'Capability':
        return (capabilities || [])
          .filter(c => !currentTags.includes(c.name))
          .map(c => ({ value: c.name, label: c.name }));
      
      default:
        return [];
    }
  }, [tags, officers, visibleArtifacts, visibleRituals, policies, capabilities]);

  // Get all tags as flat array with type info (for display)
  const getAllTagsFlat = useCallback(() => {
    const result = [];
    
    Object.entries(tags).forEach(([fieldName, values]) => {
      const tagType = FIELD_TO_TAG_TYPE[fieldName];
      if (!tagType || !values) return;
      
      values.forEach(value => {
        result.push({
          type: tagType,
          value,
          label: getTagLabel(tagType, value),
          style: TAG_STYLES[tagType],
          fieldName
        });
      });
    });
    
    return result;
  }, [tags, getTagLabel]);

  // Check if any tags are selected
  const hasAnyTags = useMemo(() => {
    return Object.values(tags).some(arr => arr && arr.length > 0);
  }, [tags]);

  // Count total tags
  const totalTagCount = useMemo(() => {
    return Object.values(tags).reduce((sum, arr) => sum + (arr?.length || 0), 0);
  }, [tags]);

  return {
    // State
    tags,
    setTags,
    currentTagType,
    setCurrentTagType,
    
    // Actions
    addTag,
    removeTag,
    resetTags,
    
    // Computed
    getTagOptions,
    getTagLabel,
    getAllTagsFlat,
    hasAnyTags,
    totalTagCount,
    visibleArtifacts,
    visibleRituals,
    
    // Constants (for convenience)
    TAG_TYPES,
    TAG_FIELD_MAP,
    TAG_STYLES,
    EMPTY_TAGS
  };
}

/**
 * Utility to convert legacy tag format to new format
 * Legacy: ["value (Type)", "value2 (Type2)"]
 * New: { tagged_officers: [], tagged_artifacts: [], ... }
 */
export function convertLegacyTags(legacyTags) {
  const result = { ...EMPTY_TAGS };
  
  if (!legacyTags || !Array.isArray(legacyTags)) return result;
  
  legacyTags.forEach(tag => {
    const match = tag.match(/^(.+)\s+\(([^)]+)\)$/);
    if (match) {
      const [, value, type] = match;
      const fieldName = TAG_FIELD_MAP[type];
      if (fieldName && !result[fieldName].includes(value)) {
        result[fieldName].push(value);
      }
    }
  });
  
  return result;
}

/**
 * Utility to convert new format to legacy format (for backwards compatibility)
 */
export function convertToLegacyTags(tags) {
  const result = [];
  
  Object.entries(tags).forEach(([fieldName, values]) => {
    const tagType = FIELD_TO_TAG_TYPE[fieldName];
    if (!tagType || !values) return;
    
    values.forEach(value => {
      result.push(`${value} (${tagType})`);
    });
  });
  
  return result;
}